import React, { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Calendar, Upload, CheckCircle } from 'lucide-react';
import Select from 'react-select';
import { toast } from 'sonner';

import axiosInstance from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import FileDropZone from '../../SharedComponents/FileDropZone';
import SieVerificationModal from '../../Modals/SieVerificationModal';
import OnboardingNavigationBar from '../OnboardingNavigationBar';

const ACCOUNTING_METHODS = [
    { value: 'accrual', label: 'Faktureringsmetoden' },
    { value: 'cash', label: 'Kontantmetoden' },
];

const VAT_PERIODS = [
    { value: 'monthly', label: 'Månadsvis' },
    { value: 'quarterly', label: 'Kvartalsvis' },
    { value: 'yearly', label: 'Årsvis' },
];

const FiscalYearStep = ({
    companyId,
    fiscalYearData,
    setFiscalYearData,
    onNext,
    onBack,
    saving,
    setSaving,
    markStepCompleted,
    alreadyCompleted,
}) => {
    const [mode, setMode] = useState('manual'); // 'manual' or 'import'
    const [sieFiles, setSieFiles] = useState([]);
    const [showSieModal, setShowSieModal] = useState(false);
    const [sieVerificationData, setSieVerificationData] = useState(null);
    const [isImporting, setIsImporting] = useState(false);

    const handleFieldChange = (field, value) => {
        setFiscalYearData(prev => ({ ...prev, [field]: value }));
    };

    const handleCreateFiscalYear = async () => {
        if (!fiscalYearData.startDate || !fiscalYearData.endDate) {
            toast.error('Ange start- och slutdatum');
            return;
        }

        try {
            setSaving(true);
            const url = getApiUrl(`${companyId}/accounting/fiscalyears`);
            await axiosInstance.post(url, {
                startDate: fiscalYearData.startDate,
                endDate: fiscalYearData.endDate,
                accounting_method: fiscalYearData.accountingMethod,
                vat_period: fiscalYearData.vatPeriod,
            });
            toast.success('Räkenskapsår skapat');
            markStepCompleted();
            onNext();
        } catch (error) {
            console.error('Error creating fiscal year:', error);
            toast.error(error.response?.data?.message || 'Kunde inte skapa räkenskapsår');
        } finally {
            setSaving(false);
        }
    };

    const handleSieFileDrop = async (files) => {
        if (!files || files.length === 0) return;

        const file = files[0];
        const formData = new FormData();
        formData.append('file', file);

        try {
            setSaving(true);
            const url = getApiUrl(`${companyId}/accounting/sie/verify`);
            const { data } = await axiosInstance.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (data.success) {
                // Structure data the same way CompanySettings does —
                // the modal reads verificationData.verification for its isValid check
                const structuredData = {
                    ...data,
                    fileId: data.fileId,
                    originalFile: file,
                    verification: data.validation,
                    preview: data.preview,
                };
                setSieVerificationData(structuredData);
                setShowSieModal(true);
            } else {
                toast.error(data.message || 'Kunde inte verifiera SIE-filen');
            }
        } catch (error) {
            console.error('Error verifying SIE file:', error);
            toast.error(error.response?.data?.message || 'Kunde inte verifiera SIE-filen');
        } finally {
            setSaving(false);
        }
    };

    const handleSieConfirmImport = async (fiscalYearSettings) => {
        try {
            setIsImporting(true);

            // Must send FormData with the original file, matching CompanySettings pattern
            const formData = new FormData();
            formData.append('file', sieVerificationData.originalFile);
            formData.append('fileId', sieVerificationData.fileId);
            formData.append('importOptions', JSON.stringify({
                fiscalYearSettings: fiscalYearSettings,
            }));

            const url = getApiUrl(`${companyId}/accounting/sie/import`);
            const { data } = await axiosInstance.post(url, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (data.success) {
                toast.success('SIE-fil importerad');
                setShowSieModal(false);
                markStepCompleted();
                onNext();
            } else {
                toast.error(data.message || 'Kunde inte importera SIE-filen');
            }
        } catch (error) {
            console.error('Error importing SIE file:', error);
            toast.error(error.response?.data?.message || 'Kunde inte importera SIE-filen');
        } finally {
            setIsImporting(false);
        }
    };

    const selectStyles = {
        control: (base, { isFocused }) => ({
            ...base,
            borderColor: isFocused ? 'var(--primary)' : '#e2e8f0',
            borderRadius: '8px',
            padding: '2px',
            fontSize: '0.875rem',
            boxShadow: isFocused ? '0 0 0 3px var(--rgba-primary-1)' : base.boxShadow,
            '&:hover': { borderColor: isFocused ? 'var(--primary)' : '#cbd5e1' },
        }),
        option: (base, { isFocused, isSelected }) => ({
            ...base,
            fontSize: '0.875rem',
            backgroundColor: isSelected ? 'var(--primary)' : isFocused ? 'var(--rgba-primary-1)' : 'white',
            color: isSelected ? 'white' : '#374151',
        }),
    };

    if (alreadyCompleted) {
        return (
            <>
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                    <CheckCircle size={48} color="#10b981" style={{ marginBottom: '1rem' }} />
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.5rem' }}>
                        Räkenskapsår finns redan
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
                        Ditt företag har redan ett räkenskapsår konfigurerat. Du kan gå vidare till nästa steg.
                    </p>
                </div>
                <OnboardingNavigationBar
                    onBack={onBack}
                    onNext={onNext}
                    nextLabel="Fortsätt"
                />
            </>
        );
    }

    return (
        <>
            <style>{`
                .fiscal-year-step {
                    max-width: 550px;
                    margin: 0 auto;
                }
                .step-header {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    margin-bottom: 0.5rem;
                }
                .step-header-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: var(--rgba-primary-1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--primary);
                }
                .step-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }
                .step-description {
                    font-size: 0.875rem;
                    color: #64748b;
                    margin-bottom: 1.5rem;
                }
                .mode-selector {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 0.75rem;
                    margin-bottom: 1.5rem;
                }
                .mode-card {
                    padding: 1rem;
                    border-radius: 12px;
                    border: 2px solid #e2e8f0;
                    cursor: pointer;
                    text-align: center;
                    transition: all 0.15s;
                    background: white;
                }
                .mode-card:hover {
                    border-color: #cbd5e1;
                    background: #f8fafc;
                }
                .mode-card.active {
                    border-color: var(--primary);
                    background: var(--rgba-primary-1);
                }
                .mode-card-icon {
                    margin-bottom: 0.5rem;
                    color: #64748b;
                }
                .mode-card.active .mode-card-icon {
                    color: var(--primary);
                }
                .mode-card-title {
                    font-weight: 600;
                    font-size: 0.875rem;
                    color: #374151;
                    margin-bottom: 0.25rem;
                }
                .mode-card-desc {
                    font-size: 0.75rem;
                    color: #94a3b8;
                    margin: 0;
                }
                .onboarding-form-group {
                    margin-bottom: 1.25rem;
                }
                .onboarding-form-label {
                    font-size: 0.8rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.375rem;
                    display: block;
                }
                .onboarding-form-input {
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.625rem 0.875rem;
                    font-size: 0.875rem;
                    width: 100%;
                    transition: border-color 0.15s;
                }
                .onboarding-form-input:focus {
                    border-color: var(--primary);
                    box-shadow: 0 0 0 3px var(--rgba-primary-1);
                    outline: none;
                }
                .date-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 1rem;
                }
            `}</style>
            <div className="fiscal-year-step">
                <div className="step-header">
                    <div className="step-header-icon">
                        <Calendar size={20} />
                    </div>
                    <h3 className="step-title">Räkenskapsår</h3>
                </div>
                <p className="step-description">
                    Skapa ditt första räkenskapsår manuellt eller importera från en SIE-fil.
                </p>

                <div className="mode-selector">
                    <div
                        className={`mode-card ${mode === 'manual' ? 'active' : ''}`}
                        onClick={() => setMode('manual')}
                    >
                        <div className="mode-card-icon"><Calendar size={24} /></div>
                        <div className="mode-card-title">Skapa manuellt</div>
                        <p className="mode-card-desc">Ange datum och inställningar</p>
                    </div>
                    <div
                        className={`mode-card ${mode === 'import' ? 'active' : ''}`}
                        onClick={() => setMode('import')}
                    >
                        <div className="mode-card-icon"><Upload size={24} /></div>
                        <div className="mode-card-title">Importera SIE</div>
                        <p className="mode-card-desc">Ladda upp en SIE-fil</p>
                    </div>
                </div>

                {mode === 'manual' && (
                    <>
                        <div className="date-row">
                            <div className="onboarding-form-group">
                                <label className="onboarding-form-label">Startdatum</label>
                                <input
                                    className="onboarding-form-input"
                                    type="date"
                                    value={fiscalYearData.startDate}
                                    onChange={(e) => handleFieldChange('startDate', e.target.value)}
                                />
                            </div>
                            <div className="onboarding-form-group">
                                <label className="onboarding-form-label">Slutdatum</label>
                                <input
                                    className="onboarding-form-input"
                                    type="date"
                                    value={fiscalYearData.endDate}
                                    onChange={(e) => handleFieldChange('endDate', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="onboarding-form-group">
                            <label className="onboarding-form-label">Bokföringsmetod</label>
                            <Select
                                options={ACCOUNTING_METHODS}
                                value={ACCOUNTING_METHODS.find(o => o.value === fiscalYearData.accountingMethod)}
                                onChange={(opt) => handleFieldChange('accountingMethod', opt.value)}
                                styles={selectStyles}
                                isSearchable={false}
                            />
                        </div>

                        <div className="onboarding-form-group">
                            <label className="onboarding-form-label">Momsperiod</label>
                            <Select
                                options={VAT_PERIODS}
                                value={VAT_PERIODS.find(o => o.value === fiscalYearData.vatPeriod)}
                                onChange={(opt) => handleFieldChange('vatPeriod', opt.value)}
                                styles={selectStyles}
                                isSearchable={false}
                            />
                        </div>

                        <OnboardingNavigationBar
                            onBack={onBack}
                            onNext={handleCreateFiscalYear}
                            nextLabel="Skapa räkenskapsår"
                            saving={saving}
                        />
                    </>
                )}

                {mode === 'import' && (
                    <>
                        <FileDropZone
                            setFiles={setSieFiles}
                            onFilesUploaded={handleSieFileDrop}
                            autoUpload={true}
                            multiple={false}
                            acceptedFileTypes=".se,.sie"
                            mainText="Dra hit din SIE-fil eller klicka för att välja"
                            subText="Accepterade format: .se, .sie"
                            minHeight="120px"
                        />

                        <SieVerificationModal
                            show={showSieModal}
                            onHide={() => setShowSieModal(false)}
                            verificationData={sieVerificationData}
                            onConfirmImport={handleSieConfirmImport}
                            onCancel={() => setShowSieModal(false)}
                            isImporting={isImporting}
                        />

                        <OnboardingNavigationBar
                            onBack={onBack}
                            onNext={onNext}
                            showSkip={true}
                            onSkip={onNext}
                            skipLabel="Hoppa över"
                            nextLabel="Fortsätt"
                            showNext={false}
                        />
                    </>
                )}
            </div>
        </>
    );
};

export default FiscalYearStep;
